import { useState, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarSrc } from "@/components/user-avatar";
import {
  Home,
  FileText,
  PawPrint,
  AlertTriangle,
  Users,
  Settings,
  Upload,
  Filter,
  User,
  LogOut,
  MessageSquare,
  Award,
  Sparkles,
  BarChart3,
  BarChart2,
  ScrollText,
  Search,
  ListChecks,
  ClipboardList,
  CalendarCheck,
  BookOpen,
  Compass,
  FolderOpen,
} from "lucide-react";
import { AppLogo, AppBrandingText } from "@/components/app-logo";
import { Link, useLocation } from "wouter";
import { getDisplayName, type User as AuthUser } from "@/lib/auth-context";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/components/theme-provider";
import { Moon, Sun } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAiEnabled } from "@/hooks/use-ai-enabled";
import { useDashboardComponents } from "@/lib/dashboard-visibility";
import { Skeleton } from "@/components/ui/skeleton";
import { GlobalSearchDialog } from "@/components/global-search";

// P23b Pass 3: derive a stable `data-tour` anchor id from each
// nav item's URL so tour authors can target the sidebar links
// directly. `/admin/tours` → `nav-sidebar-admin-tours`. Anchors
// on role-gated items only render in the DOM for users whose
// role can see them — pair the tour with a matching `roles`
// audience criterion when targeting a gated item.
function navAnchorId(url: string): string {
  return `nav-sidebar-${url.replace(/^\//, "").replace(/\//g, "-")}`;
}

/*
 * Scanner-visible enumeration of every sidebar nav anchor id. The
 * `data-tour` attribute below is computed at runtime via
 * `navAnchorId(item.url)`, so the literal-attribute regex used by
 * `scripts/scan-data-tour.ts` would otherwise miss them. Listing the
 * literals in this comment lets the scanner pick them up so they
 * surface in the admin step editor's Target ID autocomplete. Keep
 * this block in sync with `dashboardItem`, `operationsItems`,
 * `planningItems`, `insightsItems`, and `adminItems` below.
 *
 *   data-tour="nav-sidebar-dashboard"        (always visible)
 *   data-tour="nav-sidebar-reports"          (always visible)
 *   data-tour="nav-sidebar-animals"          (always visible)
 *   data-tour="nav-sidebar-interactions"     (always visible)
 *   data-tour="nav-sidebar-tasks"            (always visible)
 *   data-tour="nav-sidebar-meetings"         (always visible)
 *   data-tour="nav-sidebar-population"       (always visible)
 *   data-tour="nav-sidebar-behavior"         (always visible)
 *   data-tour="nav-sidebar-counts"           (admin only)
 *   data-tour="nav-sidebar-files"            (always visible)
 *   data-tour="nav-sidebar-summaries"        (admin only, requires AI)
 *   data-tour="nav-sidebar-admin-users"      (super-admin only)
 *   data-tour="nav-sidebar-admin-tasks"      (admin only)
 *   data-tour="nav-sidebar-admin-glossary"   (admin only)
 *   data-tour="nav-sidebar-admin-tours"      (admin only)
 *   data-tour="nav-sidebar-admin-settings"   (admin only)
 *   data-tour="nav-sidebar-logs"             (super-admin only)
 */

// Dashboard - standalone at the top
const dashboardItem = {
  title: "Dashboard",
  url: "/dashboard",
  icon: Home,
};

// Reporting section - day-to-day shelter work
const operationsItems = [
  {
    title: "Reports",
    url: "/reports",
    icon: FileText,
  },
  {
    title: "Animals",
    url: "/animals",
    icon: PawPrint,
  },
  {
    title: "Interactions",
    url: "/interactions",
    icon: MessageSquare,
  },
];

// Planning section - task management and boards
const planningItems = [
  {
    title: "Tasks",
    url: "/tasks",
    icon: ClipboardList,
  },
  {
    title: "Meetings",
    url: "/meetings",
    icon: CalendarCheck,
  },
];

// Monitoring section - behavior and population monitoring
const insightsItems = [
  {
    title: "Population",
    url: "/population",
    icon: BarChart3,
    adminOnly: false,
  },
  {
    title: "Behavior",
    url: "/behavior",
    icon: Award,
    adminOnly: false,
  },
  {
    title: "Counts",
    url: "/counts",
    icon: BarChart2,
    adminOnly: false,
    dashboardKey: "counts" as const,
  },
  {
    title: "Files",
    url: "/files",
    icon: FolderOpen,
    adminOnly: false,
  },
];

const adminItems = [
  {
    title: "AI Summaries",
    url: "/summaries",
    icon: Sparkles,
    adminOnly: true,
    requiresAi: true,
  },
  {
    title: "Users",
    url: "/admin/users",
    icon: Users,
    superAdminOnly: true,
  },
  {
    title: "Task Boards",
    url: "/admin/tasks",
    icon: ListChecks,
    adminOnly: true,
  },
  {
    title: "Glossary",
    url: "/admin/glossary",
    icon: BookOpen,
    adminOnly: true,
  },
  {
    title: "Tours",
    url: "/admin/tours",
    icon: Compass,
    adminOnly: true,
  },
  {
    title: "Settings",
    url: "/admin/settings",
    icon: Settings,
    adminOnly: true,
  },
  {
    title: "Logs",
    url: "/logs",
    icon: ScrollText,
    superAdminOnly: true,
  },
];

function SidebarUserAvatar({ user, className }: { user: AuthUser | null; className?: string }) {
  if (!user) {
    return (
      <Avatar className={className}>
        <AvatarFallback><User className="h-4 w-4" /></AvatarFallback>
      </Avatar>
    );
  }
  const displayName = getDisplayName(user);
  const src = getAvatarSrc(user.avatarUrl, user.id);
  const initials = displayName.split(/\s+/).map(p => p[0]).join("").toUpperCase().slice(0, 2) || "?";

  return (
    <Avatar className={className}>
      {src && <AvatarImage src={src} alt={displayName} />}
      <AvatarFallback>{initials}</AvatarFallback>
    </Avatar>
  );
}

interface AppSidebarProps {
  user: AuthUser | null;
  onLogout: () => void;
}

export function AppSidebar({ user, onLogout }: AppSidebarProps) {
  const [location] = useLocation();
  const { setOpenMobile, isMobile, collapsed } = useSidebar();
  const { theme, toggleTheme } = useTheme();
  const { isAiEnabled, isLoading: isAiLoading } = useAiEnabled();
  const { visible: dashboardVisible } = useDashboardComponents();
  const [searchOpen, setSearchOpen] = useState(false);
  const [seedQuery, setSeedQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      searchInputRef.current?.blur();
      return;
    }
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && !searchOpen) {
      e.preventDefault();
      setSeedQuery(e.key);
      setSearchOpen(true);
      if (searchInputRef.current) searchInputRef.current.value = "";
    }
  }, [searchOpen]);

  const handleSearchPaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    if (searchOpen) return;
    const pasted = e.clipboardData.getData("text").trim();
    if (pasted.length > 0) {
      e.preventDefault();
      setSeedQuery(pasted);
      setSearchOpen(true);
      if (searchInputRef.current) searchInputRef.current.value = "";
    }
  }, [searchOpen]);

  const handleSearchDialogChange = useCallback((open: boolean) => {
    setSearchOpen(open);
    if (!open) {
      setSeedQuery("");
      if (searchInputRef.current) searchInputRef.current.value = "";
      if (isMobile) setOpenMobile(false);
    }
  }, [isMobile, setOpenMobile]);

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const showExpandedSearch = !collapsed || isMobile;

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className={(collapsed && !isMobile) ? "border-b px-2 py-4" : "border-b px-6 py-4"}>
        <div className={(collapsed && !isMobile) ? "flex flex-col items-center gap-2" : "flex items-center justify-between gap-2"}>
          <Link href="/dashboard" className={(collapsed && !isMobile) ? "flex items-center justify-center" : "flex items-center gap-3"}>
            <AppLogo size="md" />
            {(!collapsed || isMobile) && (
              <AppBrandingText />
            )}
          </Link>
          {!showExpandedSearch && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { setSeedQuery(""); setSearchOpen(true); }}
              data-testid="button-global-search"
              aria-label="Search"
            >
              <Search className="h-4 w-4" />
            </Button>
          )}
        </div>
        {showExpandedSearch && (
          <div className="mt-2 relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              ref={searchInputRef}
              placeholder="Search..."
              className="pl-8"
              onKeyDown={handleSearchKeyDown}
              onPaste={handleSearchPaste}
              data-testid="input-sidebar-search"
            />
          </div>
        )}
      </SidebarHeader>
      
      <SidebarContent>
        {/* Dashboard - standalone at the top */}
        <SidebarGroup>
          <SidebarGroupContent className={(!collapsed || isMobile) ? "pl-2 pt-4" : "pt-4"}>
            <SidebarMenu>
              {isAiLoading ? (
                <SidebarMenuItem>
                  <div className={(collapsed && !isMobile) ? "flex justify-center py-2" : "flex items-center gap-2 px-3 py-2"}>
                    <Skeleton className="h-4 w-4 rounded" />
                    {(!collapsed || isMobile) && <Skeleton className="h-4 w-20" />}
                  </div>
                </SidebarMenuItem>
              ) : (
                (() => {
                  const isActive = location === dashboardItem.url || location.startsWith(dashboardItem.url);
                  
                  return (
                    <SidebarMenuItem className="relative">
                      {isActive && (
                        <div 
                          className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-primary rounded-r-full"
                          aria-hidden="true"
                        />
                      )}
                      <SidebarMenuButton
                        asChild
                        tooltip={dashboardItem.title}
                        data-active={isActive}
                        className={(collapsed && !isMobile) ? "justify-center" : (isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : "")}
                      >
                        <Link href={dashboardItem.url} onClick={handleNavClick} data-testid={`link-sidebar-${dashboardItem.title.toLowerCase()}`} data-tour={navAnchorId(dashboardItem.url)}>
                          <dashboardItem.icon className="h-4 w-4" />
                          {(!collapsed || isMobile) && (
                            <span>{dashboardItem.title}</span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })()
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Reporting section - day-to-day shelter work */}
        <SidebarGroup>
          {(!collapsed || isMobile) && (
            <SidebarGroupLabel className="px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Reporting
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent className={(!collapsed || isMobile) ? "pl-2" : ""}>
            <SidebarMenu>
              {isAiLoading ? (
                // Show skeletons while loading
                operationsItems
                  .filter((item) => !("adminOnly" in item) || !item.adminOnly || user?.isAdmin)
                  .map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <div className={(collapsed && !isMobile) ? "flex justify-center py-2" : "flex items-center gap-2 px-3 py-2"}>
                      <Skeleton className="h-4 w-4 rounded" />
                      {(!collapsed || isMobile) && <Skeleton className="h-4 w-20" />}
                    </div>
                  </SidebarMenuItem>
                ))
              ) : (
                operationsItems
                  .filter((item) => !("adminOnly" in item) || !item.adminOnly || user?.isAdmin)
                  .map((item) => {
                  const isActive = location === item.url || location.startsWith(item.url);
                  
                  return (
                    <SidebarMenuItem key={item.title} className="relative">
                      {isActive && (
                        <div 
                          className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-primary rounded-r-full"
                          aria-hidden="true"
                        />
                      )}
                      <SidebarMenuButton
                        asChild
                        tooltip={item.title}
                        data-active={isActive}
                        className={(collapsed && !isMobile) ? "justify-center" : (isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : "")}
                      >
                        <Link href={item.url} onClick={handleNavClick} data-testid={`link-sidebar-${item.title.toLowerCase()}`} data-tour={navAnchorId(item.url)}>
                          <item.icon className="h-4 w-4" />
                          {(!collapsed || isMobile) && (
                            <span>{item.title}</span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Monitoring section - behavior and population monitoring */}
        <SidebarGroup>
          {(!collapsed || isMobile) && (
            <SidebarGroupLabel className="px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Monitoring
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent className={(!collapsed || isMobile) ? "pl-2" : ""}>
            <SidebarMenu>
              {isAiLoading ? (
                insightsItems
                  .filter((item) => {
                    if ("dashboardKey" in item && item.dashboardKey === "counts") {
                      return dashboardVisible.has("counts");
                    }
                    return !item.adminOnly || user?.isAdmin;
                  })
                  .map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <div className={(collapsed && !isMobile) ? "flex justify-center py-2" : "flex items-center gap-2 px-3 py-2"}>
                        <Skeleton className="h-4 w-4 rounded" />
                        {(!collapsed || isMobile) && <Skeleton className="h-4 w-20" />}
                      </div>
                    </SidebarMenuItem>
                  ))
              ) : (
                insightsItems
                  .filter((item) => {
                    if ("dashboardKey" in item && item.dashboardKey === "counts") {
                      return dashboardVisible.has("counts");
                    }
                    return !item.adminOnly || user?.isAdmin;
                  })
                  .map((item) => {
                  const isActive = location === item.url || location.startsWith(item.url);
                  
                  return (
                    <SidebarMenuItem key={item.title} className="relative">
                      {isActive && (
                        <div 
                          className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-primary rounded-r-full"
                          aria-hidden="true"
                        />
                      )}
                      <SidebarMenuButton
                        asChild
                        tooltip={item.title}
                        data-active={isActive}
                        className={(collapsed && !isMobile) ? "justify-center" : (isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : "")}
                      >
                        <Link href={item.url} onClick={handleNavClick} data-testid={`link-sidebar-${item.title.toLowerCase()}`} data-tour={navAnchorId(item.url)}>
                          <item.icon className="h-4 w-4" />
                          {(!collapsed || isMobile) && (
                            <span>{item.title}</span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Planning section - task management */}
        <SidebarGroup>
          {(!collapsed || isMobile) && (
            <SidebarGroupLabel className="px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Planning
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent className={(!collapsed || isMobile) ? "pl-2" : ""}>
            <SidebarMenu>
              {isAiLoading ? (
                planningItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <div className={(collapsed && !isMobile) ? "flex justify-center py-2" : "flex items-center gap-2 px-3 py-2"}>
                      <Skeleton className="h-4 w-4 rounded" />
                      {(!collapsed || isMobile) && <Skeleton className="h-4 w-20" />}
                    </div>
                  </SidebarMenuItem>
                ))
              ) : (
                planningItems.map((item) => {
                  const isActive = location === item.url || location.startsWith(item.url);
                  
                  return (
                    <SidebarMenuItem key={item.title} className="relative">
                      {isActive && (
                        <div 
                          className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-primary rounded-r-full"
                          aria-hidden="true"
                        />
                      )}
                      <SidebarMenuButton
                        asChild
                        tooltip={item.title}
                        data-active={isActive}
                        className={(collapsed && !isMobile) ? "justify-center" : (isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : "")}
                      >
                        <Link href={item.url} onClick={handleNavClick} data-testid={`link-sidebar-${item.title.toLowerCase()}`} data-tour={navAnchorId(item.url)}>
                          <item.icon className="h-4 w-4" />
                          {(!collapsed || isMobile) && (
                            <span>{item.title}</span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Administration section — hidden entirely for staff users */}
        {user?.isAdmin && <SidebarGroup>
          {/* Gate E3.7: Hide group label when collapsed (desktop only) */}
          {(!collapsed || isMobile) && (
            <SidebarGroupLabel className="px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Administration
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent className={(!collapsed || isMobile) ? "pl-2" : ""}>
            <SidebarMenu>
              {isAiLoading ? (
                // Show skeletons for all admin items while loading
                adminItems
                  .filter((item) => !item.adminOnly || user?.isAdmin)
                  .filter((item) => !("superAdminOnly" in item) || user?.isSuperAdmin)
                  .map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <div className={(collapsed && !isMobile) ? "flex justify-center py-2" : "flex items-center gap-2 px-3 py-2"}>
                        <Skeleton className="h-4 w-4 rounded" />
                        {(!collapsed || isMobile) && <Skeleton className="h-4 w-20" />}
                      </div>
                    </SidebarMenuItem>
                  ))
              ) : (
                adminItems
                  .filter((item) => !item.adminOnly || user?.isAdmin)
                  .filter((item) => !("superAdminOnly" in item) || user?.isSuperAdmin)
                  .filter((item) => !item.requiresAi || isAiEnabled)
                  .map((item) => {
                    const isActive = location === item.url || location.startsWith(item.url);
                    
                    return (
                      <SidebarMenuItem key={item.title} className="relative">
                        {isActive && (
                          <div 
                            className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-primary rounded-r-full"
                            aria-hidden="true"
                          />
                        )}
                        <SidebarMenuButton
                          asChild
                          tooltip={item.title}
                          data-active={isActive}
                          className={(collapsed && !isMobile) ? "justify-center" : (isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : "")}
                        >
                          <Link href={item.url} onClick={handleNavClick} data-testid={`link-sidebar-${item.title.toLowerCase()}`} data-tour={navAnchorId(item.url)}>
                            <item.icon className="h-4 w-4" />
                            {/* Gate E3.7: Hide text when collapsed (desktop only) */}
                            {(!collapsed || isMobile) && <span>{item.title}</span>}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>}
      </SidebarContent>

      <SidebarFooter className="border-t px-3 py-3">
        {/* Gate E3.7: Unified footer with user account + theme toggle (icons-only when collapsed on desktop) */}
        <div className={(collapsed && !isMobile) ? "flex flex-col items-center gap-2" : "flex items-center justify-between gap-2"}>
          {/* User account dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className={(collapsed && !isMobile) ? "h-8 w-8 p-0 hover-elevate active-elevate-2" : "flex-1 min-w-0 justify-start hover-elevate active-elevate-2"} 
                data-testid="button-user-menu"
              >
                {(collapsed && !isMobile) ? (
                  <SidebarUserAvatar user={user} className="h-8 w-8" />
                ) : (
                  <div className="flex items-center gap-3 w-full">
                    <SidebarUserAvatar user={user} className="h-8 w-8 flex-shrink-0" />
                    <div className="flex-1 overflow-hidden text-left">
                      <p className="text-sm font-medium truncate" data-testid="text-user-display-name">{getDisplayName(user)}</p>
                      <p className="text-xs text-muted-foreground truncate" data-testid="text-user-email">{user?.email || ''}</p>
                    </div>
                  </div>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>My Account</span>
                <Badge 
                  variant={user?.isAdmin ? "default" : "secondary"} 
                  className="text-xs"
                  data-testid="badge-user-role"
                >
                  {user?.role === "admin" ? "Admin" : "Staff"}
                </Badge>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild onClick={handleNavClick}>
                <Link href="/account" data-testid="menu-item-profile">
                  <User className="mr-2 h-4 w-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={toggleTheme} data-testid="menu-item-theme-toggle">
                {theme === "light" ? (
                  <Moon className="mr-2 h-4 w-4" />
                ) : (
                  <Sun className="mr-2 h-4 w-4" />
                )}
                {theme === "light" ? "Dark Mode" : "Light Mode"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={onLogout} data-testid="menu-item-logout">
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </SidebarFooter>
      <GlobalSearchDialog open={searchOpen} onOpenChange={handleSearchDialogChange} initialQuery={seedQuery} />
    </Sidebar>
  );
}